import type { MediaImage } from "@shopify/hydrogen/storefront-api-types";
import { useCarousel } from "@models/interactions/use-carousel/index";
import { ArrowNext, ArrowPrev } from "./arrows.client";
import { Carousel } from "react-responsive-carousel";
import { useMemo, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { CloseIcon } from "@components/atoms/Icons";
import { Fragment } from "react";

interface ProductCarousel {
  images: MediaImage[];
}

function useProductCarousel(images: ProductCarousel["images"]) {
  const validImages = images.filter((item) => Boolean(item.image?.url));
  const { models, operations } = useCarousel(images.length);
  const { nextSlide, prevSlide, setCurrentSlide } = operations;
  const { currentSlide, hasNext, hasPrev } = models;
  const [modal, setModal] = useState(false);

  function handleChangeOpen() {
    setModal(true);
  }

  function handleChangeClose() {
    setModal(false);
  }

  const imgElements = useMemo(
    () =>
      validImages.map((data, index) => (
        <div
          key={index}
          className={`w-full h-full cursor-zoom-in ${modal && "hover:scale-125"}`}
        >
          <img
            src={data.image?.url}
            alt={data.alt ?? ""}
            className="h-[375px] object-contain"
          />
        </div>
      )),
    [validImages]
  );

  const thumbs = useMemo(
    () =>
      validImages.map((data, index) => {
        const selected = index === currentSlide;
        return (
          <button
            key={index}
            onClick={() => setCurrentSlide(index)}
            className={`border-solid border-2 h-[58px] w-[58px] ${
              selected ? "border-black" : "border-gray-300"
            }`}
          >
            <img
              src={data.image?.url}
              alt={data.alt ?? ""}
              className="h-full w-full object-contain"
            />
          </button>
        );
      }),
    [validImages]
  );

  return {
    models: {
      validImages,
      imgElements,
      thumbs,
      hasNext,
      hasPrev,
      currentSlide,
      modal,
    },
    operations: {
      handleChangeOpen,
      handleChangeClose,
      nextSlide,
      prevSlide,
    },
  };
}

export function ProductCarousel({ images }: ProductCarousel) {
  const { models, operations } = useProductCarousel(images);

  return (
    <>
      <div className="lg:justify-self-end lg:mr-24">
        {!models.modal && (
          <>
            <div className="grid grid-cols-[1fr_4fr_1fr] items-center justify-items-center">
              <ArrowPrev
                onClick={operations.prevSlide}
                canContinue={models.hasPrev}
              />
              <div onClick={operations.handleChangeOpen}>
                <Carousel
                  swipeable
                  preventMovementUntilSwipeScrollTolerance
                  swipeScrollTolerance={50}
                  showStatus={false}
                  showArrows={false}
                  showThumbs={false}
                  showIndicators={false}
                  selectedItem={models.currentSlide}
                >
                  {models.imgElements}
                </Carousel>
              </div>
              <ArrowNext
                onClick={operations.nextSlide}
                canContinue={models.hasNext}
              />
            </div>
            <div className="grid grid-cols-5 gap-x-2 lg:flex mt-5 gap-y-2 mb-4 lg:justify-center lg:mb-0">
              {models.thumbs}
            </div>
          </>
        )}
      </div>
      <Transition appear show={models.modal} as={Fragment}>
        <Dialog
          as="div"
          className="relative overflow-y-scroll h-screen"
          onClose={operations.handleChangeClose}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black z-40 bg-opacity-25" />
          </Transition.Child>

          <div className="fixed z-50 inset-0 top-0">
            <div className="flex items-center justify-center min-h-full p-0 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full min-w-[340px] max-w-[750px] overflow-y-auto h-screen md:h-auto px-5 lg:p-8 md:p-24 pt-16 lg:py-24 text-left align-middle transition-all transform bg-white shadow-xl relative">
                  <div className="flex flex-row">
                    <button
                      onClick={operations.handleChangeClose}
                      className="absolute cursor-pointer top-8 right-8"
                    >
                      <div className="lg:flex hidden">
                        <CloseIcon
                          className="font-medium"
                          width="20"
                          height="20"
                        />
                      </div>
                      <div className="lg:hidden flex">
                        <CloseIcon
                          className="font-medium"
                          width="24"
                          height="24"
                        />
                      </div>
                    </button>
                    <div>
                      <div className="grid grid-cols-[1fr_4fr_1fr] items-center justify-items-center">
                        <ArrowPrev
                          onClick={operations.prevSlide}
                          canContinue={models.hasPrev}
                        />
                        <Carousel
                          swipeable
                          preventMovementUntilSwipeScrollTolerance
                          swipeScrollTolerance={50}
                          showStatus={false}
                          showArrows={false}
                          showThumbs={false}
                          showIndicators={false}
                          selectedItem={models.currentSlide}
                        >
                          {models.imgElements}
                        </Carousel>
                        <ArrowNext
                          onClick={operations.nextSlide}
                          canContinue={models.hasNext}
                        />
                      </div>
                      <div className="grid grid-cols-5 gap-x-2 lg:flex mt-5 gap-y-2 mb-4 lg:justify-center lg:mb-0">
                        {models.thumbs}
                      </div>
                    </div>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </>
  );
}